/**
 * Worker Badge Component
 *
 * Displays a worker's initials with task-specific color and completion status
 */

import React from 'react'
import { Check } from 'lucide-react'
import type { TaskType, WorkerAssignmentLog } from 'src/store/todo/types'
import type { WorkerZoneAssignment } from '../../types'
import {
  getTaskColor,
  isValueTypeTask,
  getAssignmentZoneTotalValue,
  getZoneLevelTotal,
  formatNumber,
  getInitials,
} from '../../utils'

type WorkerBadgeProps = {
  /** Worker's first name */
  firstName: string
  /** Worker's last name */
  lastName: string
  /** Worker assignment data for this zone */
  assignment: WorkerZoneAssignment
  /** Zone ID */
  zoneId: string
  /** Array of all task types */
  tasks: TaskType[]
  /** Map of task IDs to TaskType objects */
  tasksMap: Record<string, TaskType>
  /** Array of all worker assignment logs */
  logs: WorkerAssignmentLog[]
  /** Callback when badge is clicked */
  onClick: () => void
}

/**
 * WorkerBadge component showing worker initials with task color
 *
 * @example
 * <WorkerBadge
 *   firstName="John"
 *   lastName="Doe"
 *   assignment={assignmentData}
 *   zoneId="zone-123"
 *   tasks={allTasks}
 *   tasksMap={taskMap}
 *   logs={allLogs}
 *   onClick={handleClick}
 * />
 */
const WorkerBadge: React.FC<WorkerBadgeProps> = ({
  firstName,
  lastName,
  assignment,
  zoneId,
  tasks,
  tasksMap,
  logs,
  onClick,
}) => {
  const { workerId, taskTypeId, locationCount, completedLocationCount, hasZoneLevelAssignment } =
    assignment

  const bgColor = getTaskColor(taskTypeId, tasks)
  const initials = getInitials(firstName, lastName)
  const hasLocationData = locationCount > 0
  const hasMixedCompletion =
    hasLocationData && completedLocationCount > 0 && completedLocationCount < locationCount
  const hasSingleLocationComplete =
    hasLocationData && locationCount === 1 && completedLocationCount === 1
  const showCompletionCount =
    (hasZoneLevelAssignment && completedLocationCount > 0) ||
    hasMixedCompletion ||
    hasSingleLocationComplete

  // Check for completion logs or value logs
  const hasCompletionLogs = logs.some(
    (log) =>
      log.workerId === workerId &&
      log.taskTypeId === taskTypeId &&
      log.zoneId === zoneId &&
      !log.softDeleted
  )

  const hasValueLogs =
    isValueTypeTask(taskTypeId, tasksMap) &&
    (getAssignmentZoneTotalValue(logs, workerId, taskTypeId, zoneId) > 0 ||
      getZoneLevelTotal(logs, workerId, taskTypeId, zoneId) > 0)

  const zoneTotalValue = getAssignmentZoneTotalValue(logs, workerId, taskTypeId, zoneId)
  const zoneLevelValue = getZoneLevelTotal(logs, workerId, taskTypeId, zoneId)
  const isValueTask = isValueTypeTask(taskTypeId, tasksMap)

  return (
    <div className="flex items-center gap-1">
      <div className="relative">
        <button
          onClick={onClick}
          className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white shadow-sm leading-none hover:opacity-80 hover:scale-110 transition-all cursor-pointer"
          style={{ backgroundColor: bgColor }}
          title={`${firstName} ${lastName}`}
        >
          {initials}
        </button>

        {/* Checkmark overlay for completed or value-logged assignments */}
        {(hasCompletionLogs || hasValueLogs) && (
          <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5 flex items-center justify-center">
            <Check size={10} className="text-white" strokeWidth={3} />
          </div>
        )}
      </div>

      {/* Completion count display */}
      {showCompletionCount && (
        <span className="text-[9px] text-gray-600 font-medium leading-none inline-flex items-center">
          {hasZoneLevelAssignment || (locationCount === 1 && completedLocationCount === 1)
            ? `(${completedLocationCount})`
            : `(${completedLocationCount}/${locationCount})`}
        </span>
      )}

      {/* Value display for harvest tasks */}
      {isValueTask && (
        <>
          {zoneTotalValue > 0 && (
            <span className="text-[9px] text-green-600 font-semibold leading-none">
              ({formatNumber(zoneTotalValue)})
            </span>
          )}
          {zoneLevelValue > 0 && (
            <span className="text-[9px] text-green-600 font-semibold leading-none">
              ({formatNumber(zoneLevelValue)})
            </span>
          )}
        </>
      )}
    </div>
  )
}

export default WorkerBadge
